import { useEffect, useRef, useState } from 'react';
import { rpc } from './supabase';
import { formatDate } from './components';
import type { SafetyRow } from './types';

type Hold = { id: string; reason_code: string; member_explanation: string; expires_at: string; released_at: string | null; appeal_explanation: string | null };

export function SafetyAction({incident,close,completed}: {incident: SafetyRow; close: () => void; completed: () => void}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [holds,setHolds] = useState<Hold[]>([]);
  const [loading,setLoading] = useState(true);
  const [working,setWorking] = useState(false);
  const [error,setError] = useState('');
  const [loadFailed,setLoadFailed] = useState(false);
  const [reason,setReason] = useState('dangerous_driving');
  const [hours,setHours] = useState(24);
  const [note,setNote] = useState('');
  useEffect(() => { dialog.current?.showModal(); }, []);
  useEffect(() => {
    let live = true;
    void rpc<Hold[]>('admin_driver_safety_holds',{p_incident: incident.id})
      .then(rows => { if(live) setHolds(rows); })
      .catch(caught => { if(live) { setLoadFailed(true); setError(caught instanceof Error ? caught.message : 'Unable to load holds. Close and reopen to retry.'); } })
      .finally(() => { if(live) setLoading(false); });
    return () => { live=false; };
  },[incident.id]);
  const active = holds.find(hold => !hold.released_at && new Date(hold.expires_at).getTime()>Date.now());
  async function submit() {
    setWorking(true); setError('');
    try {
      if(active) await rpc('admin_release_driver_safety_hold',{p_hold:active.id,p_explanation:note.trim()});
      else await rpc('admin_apply_driver_safety_hold',{
        p_incident:incident.id,p_reason:reason,p_explanation:note.trim(),p_hours:hours,
      });
      completed();
    } catch(caught) { setError(caught instanceof Error ? caught.message : 'The action could not be confirmed. Refresh before retrying.'); }
    finally { setWorking(false); }
  }
  return <dialog ref={dialog} className="modal review-modal native-modal" aria-labelledby="safety-hold-title"
    onCancel={event => { event.preventDefault(); if(!working) close(); }}>
    <button className="modal-close" aria-label="Close" disabled={working} onClick={close}>×</button>
    <h2 id="safety-hold-title">{active ? 'Review driving hold' : 'Temporary driving hold'}</h2>
    <p>Incident {incident.id.slice(0,8)} · {incident.subject_name ?? 'Member'}. A hold pauses new offers and ride starts. Access to records, support and an already active ride remains available.</p>
    {loading ? <p role="status">Loading existing actions…</p> : <>
      {holds.map(hold => <blockquote key={hold.id}><p>{hold.member_explanation}</p>
        <small>{hold.released_at ? 'Released' : `Expires ${formatDate(hold.expires_at,true)} IST`}</small>
        {hold.appeal_explanation && <p><strong>Member review request: </strong>{hold.appeal_explanation}</p>}
      </blockquote>)}
      {!active && <>
        <label>Evidence-based concern<select value={reason} onChange={e=>setReason(e.target.value)}>
          <option value="dangerous_driving">Dangerous driving</option><option value="identity_mismatch">Identity mismatch</option>
          <option value="intoxication">Driving under the influence</option><option value="threats">Threats</option>
        </select></label>
        <label>Review window<select value={hours} onChange={e=>setHours(Number(e.target.value))}>
          <option value={6}>6 hours</option><option value={24}>24 hours</option><option value={48}>48 hours</option><option value={72}>72 hours</option>
        </select></label>
      </>}
      <label>Explanation shown to the member<textarea minLength={20} maxLength={500} value={note} onChange={e=>setNote(e.target.value)}
        placeholder="State the rule and facts supporting the action. Exclude reporter identity and private evidence." /></label>
      <div className="modal-actions"><button className="secondary-button" disabled={working} onClick={close}>Cancel</button>
        <button className="primary-button" disabled={working || loadFailed || note.trim().length<20} onClick={()=>void submit()}>
          {working ? 'Recording…' : active ? 'Release this hold' : 'Apply temporary hold'}
        </button></div>
    </>}
    {error && <p role="alert" className="inline-error">{error}</p>}
  </dialog>;
}
